import React, { FC, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { graphql } from 'react-relay';
import { useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import AreaDesigner from 'src/components/widgets/areaDesigner/areaDesigner';
import Loader from 'src/components/widgets/loader';
import { ProductStatus } from 'src/shared/enums';
import { T, stringFormat } from 'src/shared/helpers';
import { useUnsavedChanges } from 'src/shared/hooks/useUnsavedChanges';
import styled from 'styled-components';
import { Area } from '../../../../widgets/areaDesigner/areaDesigner.model';
import { Dialog, DialogWindow, MessageDialog, useDialogManager } from '../../../../widgets/dialog';
import { DisabledModelSide, useProductConfigurationState } from '../../state';
import { ModelColor } from '../../state/model/modelColor';
import { ModelOptions } from '../../state/model/modelOptions';
import { ModelSide } from '../../state/model/modelSide';
import { printAreaEditorDialogMutation } from './__generated__/printAreaEditorDialogMutation.graphql';
import { printAreaEditorDialogQuery } from './__generated__/printAreaEditorDialogQuery.graphql';

const LoaderWrapper = styled.div`
    position: relative;
    top: 34%;
    text-align: -webkit-center;
`;

const CustomDialogWindow = styled(DialogWindow)`
    flex: 1;
    align-self: stretch;
    justify-self: stretch;
    margin: 0 80px;
    padding: 16px 40px 20px;

    > svg:first-child {
        z-index: 2003;
    }
`;

const DialogHeader = styled.div`
    margin-bottom: 10px;
`;

const DialogHeaderTop = styled.div`
    display: flex;
    align-items: baseline;
    gap: 12px;
`;

const DialogHeaderTitle = styled.h1`
    color: #263238;
    font-size: 16px;
    font-weight: 600;
    margin: 0;
`;

const DialogHeaderSubtitle = styled.div`
    font-size: 13px;
    color: #71717a;
    margin-top: 2px;
`;

const GuideLink = styled.a`
    color: ${(props) => props.theme.app.component.link.color};
    font-size: 12px;
    font-weight: 500;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

interface PrintAreaEditorDialogProps {
    modelColor: ModelColor;
    modelSide: ModelSide;
    modelOptions: ModelOptions | null;
    disabledModelSides: DisabledModelSide[];
    onSave: (modelSide: ModelSide, modelOptions: ModelOptions | null, isDirty: boolean, areasWereDeleted: boolean) => void;
    onClose: () => void;
}

interface PrintAreaEditorDialogContentProps extends PrintAreaEditorDialogProps {
    onHasChanges: () => void;
    editorSaveRef: React.MutableRefObject<() => void>;
}

const PrintAreaEditorDialogContent: FC<PrintAreaEditorDialogContentProps> = (props) => {
    const [modelSide, setModelSide] = useState(props.modelSide);
    const modelSideRef = useRef(props.modelSide);
    const { state } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();

    const query = useLazyLoadQuery<printAreaEditorDialogQuery>(
        graphql`
            query printAreaEditorDialogQuery($productIDs: [String!]) {
                printAreaTemplates {
                    templateID
                    name
                    width
                    height
                }
                productIDsWithTemplates(productIDs: $productIDs) {
                    productID
                    templateCount
                }
            }
        `,
        { productIDs: state.product.id ? [state.product.id.toString()] : [] }
    );

    const productHasTemplates = (query.productIDsWithTemplates?.length ?? 0) > 0 && query.productIDsWithTemplates!.some((x) => x.templateCount > 0);

    const [commiCreateTemplate, isSavingTemplate] = useMutation<printAreaEditorDialogMutation>(graphql`
        mutation printAreaEditorDialogMutation($input: PrintAreaTemplateCreateInput!) {
            createPrintAreaTemplate(input: $input) {
                printAreaTemplate {
                    templateID
                    name
                    width
                    height
                }
            }
        }
    `);

    const findTemplateForArea = (area: Area) => {
        const areaWidthCm = area.boundsWidth / modelSide.ppcm;
        const areaHeightCm = area.boundsHeight / modelSide.ppcm;
        return query.printAreaTemplates.find((x) => Math.abs(x!.width - areaWidthCm) < 0.01 && Math.abs(x!.height - areaHeightCm) < 0.01);
    };

    const performSave = (modelOptions: ModelOptions | null, isDirty: boolean, areasWereDeleted: boolean) => {
        const sideToSave = modelSideRef.current;
        sideToSave.areas = sideToSave.areas.filter((x) => x.boundsWidth !== 0 && x.boundsHeight !== 0);

        for (const area of sideToSave.areas) {
            const template = findTemplateForArea(area);
            if (!template) {
                const areaWidthCm = area.boundsWidth / sideToSave.ppcm;
                const areaHeightCm = area.boundsHeight / sideToSave.ppcm;

                commiCreateTemplate({
                    onCompleted: (response) => null,
                    onError: (response) => null,
                    updater: (store) => store.invalidateStore(),
                    variables: {
                        input: {
                            name: area.name != '' ? area.name : T._('Custom size', 'Admin'),
                            width: areaWidthCm,
                            height: areaHeightCm
                        }
                    }
                });
            }
        }

        props.onSave(sideToSave, modelOptions, isDirty, areasWereDeleted);
    };

    const handleSaveClick = (modelOptions: ModelOptions | null, isDirty: boolean, areasWereDeleted: boolean, deletedAreasCount: number) => {
        const sideToSave = modelSideRef.current;
        sideToSave.areas = sideToSave.areas.filter((x) => x.boundsWidth !== 0 && x.boundsHeight !== 0);

        if (sideToSave.areas.length === 0) {
            showDialog('no-areas', <MessageDialog message={T._('Add at last one print area.', 'Admin')} />);
            return;
        }

        const isPublished = state.product.statusID === ProductStatus.Published;

        if (areasWereDeleted && deletedAreasCount > 0 && state.product.id && (productHasTemplates || isPublished)) {
            const dialogId = productHasTemplates ? 'areas-deleted-warning' : 'areas-deleted-orders-warning';

            showDialog(
                dialogId,
                <Dialog
                    buttons={[
                        {
                            label: productHasTemplates ? T._('Cancel', 'Admin') : T._('No', 'Admin'),
                            outline: true,
                            onClick: () => {
                                closeDialog(dialogId);
                                props.onClose();
                            }
                        },
                        {
                            label: productHasTemplates ? T._('Save', 'Admin') : T._('Yes', 'Admin'),
                            onClick: () => {
                                closeDialog(dialogId);
                                performSave(modelOptions, isDirty, areasWereDeleted);
                            }
                        }
                    ]}
                >
                    {productHasTemplates && (
                        <>
                            <p>
                                {stringFormat(T._('We have detected the deletion of {0} print area(s).', 'Admin'), deletedAreasCount.toString())}
                                <br />
                                {T._('This action may affect any pre-designed templates associated with this product.', 'Admin')}
                            </p>
                            <p>
                                {T._(
                                    'Items linked to the deleted print area(s) will not be removed. Instead, they will remain in their original position, which may cause them to become partially or completely invisible.',
                                    'Admin'
                                )}
                            </p>
                            <p>
                                {T._(
                                    'If you proceed with saving, all affected templates will need to be reviewed and updated manually. For more information, please see the following',
                                    'Admin'
                                )}{' '}
                                <a
                                    href='https://zakeke.zendesk.com/hc/en-us/articles/27271591385756-Consequences-of-print-area-removal-on-products-with-configured-pre-designed-templates'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    {T._('guide', 'Admin')}
                                </a>
                                .
                            </p>
                        </>
                    )}
                    {productHasTemplates && isPublished && (
                        <p>
                            <strong>
                                {T._(
                                    'Additionally, if there are orders currently being processed for this product, removing print areas may cause issues.',
                                    'Admin'
                                )}
                            </strong>
                        </p>
                    )}
                    {!productHasTemplates && (
                        <>
                            <p>{stringFormat(T._('We have detected the deletion of {0} print area(s).', 'Admin'), deletedAreasCount.toString())}</p>
                            <p>
                                <strong>
                                    {T._(
                                        'By saving these changes, if there are orders currently being processed for this product, you may experience issues with them.',
                                        'Admin'
                                    )}
                                </strong>
                            </p>
                            <p>{T._('Do you want to continue?', 'Admin')}</p>
                        </>
                    )}
                </Dialog>
            );
            return;
        }

        performSave(modelOptions, isDirty, areasWereDeleted);
    };

    return (
        <AreaDesigner
            css={`height: 100%; !important; width: 100% !important;`}
            modelSide={modelSide}
            modelColor={props.modelColor}
            modelOptions={props.modelOptions}
            disabledModelSides={props.disabledModelSides}
            onModelSideChanged={(modelSide) => {
                modelSideRef.current = modelSide;
                setModelSide(modelSide);
                props.onHasChanges();
            }}
            onDirtyChange={(isDirty) => {
                if (isDirty) props.onHasChanges();
            }}
            onSaveClick={handleSaveClick}
            onRegisterSave={(save) => (props.editorSaveRef.current = save)}
        />
    );
};

export const PrintAreaEditorDialog: FC<PrintAreaEditorDialogProps> = (props) => {
    const { state } = useProductConfigurationState();
    const [hasChanges, setHasChanges] = useState(false);
    const [exitGuardPortalTarget, setExitGuardPortalTarget] = useState<HTMLDivElement | null>(null);
    const editorSaveRef = useRef<() => void>(() => {});
    const { resetSnapshot, saveRef, guardAction, NavigationGuard } = useUnsavedChanges(hasChanges, {
        portalTarget: exitGuardPortalTarget,
        zIndexBase: 2099,
        message: T._('If you leave now, your changes will be lost. Please use "Save" to keep the changes or "Discard" to cancel them.', 'Admin')
    });

    const side = useMemo(
        () => (props.modelSide ? state.sides.find((x) => x.guid == props.modelSide.originalSideGuid) : null),
        [props.modelSide?.originalSideGuid]
    );

    const handleHasChanges = useCallback(() => {
        setHasChanges(true);
    }, []);

    useEffect(() => {
        resetSnapshot();
    }, [resetSnapshot]);

    saveRef.current = () => editorSaveRef.current();

    // Chiusura: se ci sono modifiche non salvate mostra il banner "unsaved changes" invece di uscire.
    const handleRequestClose = () => {
        if (!guardAction(props.onClose)) props.onClose();
    };

    return (
        <Dialog
            titleHtml={
                <>
                    <DialogHeader>
                        <DialogHeaderTop>
                            <DialogHeaderTitle>{T._('Area editor', 'Admin')}</DialogHeaderTitle>
                            <GuideLink
                                href='https://zakeke.zendesk.com/hc/en-us/articles/360016879139-How-to-define-a-Print-Area'
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                {T._('View guide', 'Admin')}
                            </GuideLink>
                        </DialogHeaderTop>
                        <DialogHeaderSubtitle>{props.modelColor.name + ' | ' + side!.name}</DialogHeaderSubtitle>
                    </DialogHeader>
                    <div ref={setExitGuardPortalTarget} />
                    {NavigationGuard}
                </>
            }
            onClose={handleRequestClose}
            windowDecorator={CustomDialogWindow}
            buttons={[]}
        >
            <Suspense
                fallback={
                    <LoaderWrapper>
                        <Loader />
                    </LoaderWrapper>
                }
            >
                <PrintAreaEditorDialogContent {...props} onHasChanges={handleHasChanges} editorSaveRef={editorSaveRef} />
            </Suspense>
        </Dialog>
    );
};
